import { ChartDonut, ChartTooltip } from "@patternfly/react-charts";
import LeafRectangleLoader from "../../LeafLoadingSkeleton/LeafRectangleLoader/LeafRectangleLoader";
import "./LeafDonutChart.scss";
import LeafGreyDonutChart from "../LeafGreyDonutChart/LeafGreyDonutChart";

type Value = {
  Actual: number;
  Formatted: string;
};

type Data = {
  Label: string;
  Percentage: Value;
  Value?: Value;
};

type LegendContent = {
  Label: string;
  Text: string;
  Color: string;
};

export type Props = {
  isLoading: boolean;
  content: LegendContent[];
  data: Data[];
  showLegend?: boolean;
  showCompactView?: boolean;
};

const LeafDonutChart = ({ isLoading, data, content, showLegend = true, showCompactView = false }: Props) => {
  const chartData = data.map((item) => ({
    x: item.Label,
    y: item.Percentage.Actual,
  }));

  const totalY = chartData.reduce((sum, item) => sum + item.y, 0);

  const colorScale = data.map((d) => {
    const match = content.find((c) => c.Label === d.Label);
    return match ? match.Color : "#F0F0F0"; // fallback color
  });

  const renderLabels = (datum: any) => {
    const dataItem = data.find((item) => item.Label === datum.x);
    const tooltipText = dataItem ? `${dataItem.Percentage.Formatted}` : "-";
    return tooltipText;
  };

  if (isLoading) {
    return (
      <div className="leaf-donut-chart">
        <div className="leaf-donut-chart__chart">
          <ChartDonut
            constrainToVisibleArea
            data={[{ x: "0", y: 100 }]}
            labels={() => null}
            colorScale={["#F0F0F0"]}
            innerRadius={70}
            padding={{
              left: 25,
              right: 25,
            }}
          />
        </div>
        {showLegend && (
          <div className="leaf-donut-chart__legend leaf-donut-chart__legend-loader">
            <LeafRectangleLoader isLoading height={"1.8rem"} width={"100%"} numRows={4} margin={"0rem"} />
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="leaf-donut-chart">
      <div className="leaf-donut-chart__chart" aria-hidden="true">
        {totalY === 0 ? (
          <LeafGreyDonutChart />
        ) : (
          <ChartDonut
            constrainToVisibleArea
            data={chartData}
            labels={({ datum }) => renderLabels(datum)}
            labelComponent={
              <ChartTooltip
                style={{ fontSize: 10 }}
                flyoutStyle={{ fill: "#000" }}
                flyoutPadding={4}
                pointerLength={0}
                constrainToVisibleArea={true}
              />
            }
            colorScale={colorScale}
            innerRadius={70}
            padding={{
              left: 25,
              right: 25,
            }}
          />
        )}
      </div>
      {showLegend && (
        <ul className={`leaf-donut-chart__legend leaf-donut-chart__legend${showCompactView ? "--compact" : ""}`}>
          {data.map((legend) => {
            const matchedContent = content.find((item) => item.Label === legend.Label);
            const fillColor = matchedContent ? matchedContent.Color : "#F0F0F0";
            const legendText = matchedContent ? matchedContent.Text : legend.Label;

            return (
              <li className="leaf-donut-chart__legend__label" key={legend.Label}>
                <span className="leaf-donut-chart__legend__label__icon">
                  <svg viewBox="0 0 12 13" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <circle cx="6" cy="6.00146" r="6" fill={fillColor} />
                  </svg>
                  <span className="leaf-donut-chart__legend__label__text">{legendText}</span>
                </span>
                <span className="leaf-donut-chart__legend__label__text">
                  {legend.Value && `(${legend.Value.Formatted})`} {legend.Percentage.Formatted}
                </span>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default LeafDonutChart;
